import {
  Job as JobProto,
  Status as StatusProto,
} from '@taxfyle/api-internal/internal/job_pb'
import type { JobGistProps } from '@/features/jobs/JobGist'
import {
  type CollaboratorProps,
  type CollaboratorRole,
  type CollaboratorType,
  type JobScopeItem,
} from '@/features/jobs/details/SubmittedJob'
import {
  type JobLegend,
  JobLegendRatingConfig,
  JobLegendTxfConfig,
} from '@/features/jobs/job-legend'
import type { PrimeUserFn } from '@/features/iam'
import type { JobStatus } from '@/features/jobs/job-status'
import { mapConsultationProtoToConsultation } from '@/features/jobs/consultations/consultation-mappers'
import type { JobTypeProps } from '@/features/jobs/job-type'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'

export function mapJobProtoToGistProps(proto: JobProto): JobGistProps {
  return {
    id: proto.getId(),
    shortId: proto.getShortId(),
    workspaceId: proto.getWorkspaceId(),
    title: mapJobProtoToTitle(proto),
    status: mapJobProtoStatusToStatus(proto.getStatus()),
  }
}

export function mapMemberProtoToCollaboratorProps(
  primeUser: PrimeUserFn,
  proto: JobProto.Member,
): CollaboratorProps {
  return {
    type: mapJobMemberTypeProtoToCollaboratorType(proto.getType()),
    role: mapJobMemberRoleTypeProtoToCollaboratorRole(proto.getRole()),
    user: primeUser({ id: proto.getId() }),
  }
}

function mapJobMemberRoleTypeProtoToCollaboratorRole(
  proto: JobProto.MemberRole,
): CollaboratorRole {
  switch (proto) {
    case JobProto.MemberRole.CHAMPION:
      return 'Champion'
    case JobProto.MemberRole.SUPPORTING:
      return 'Supporting'
    default:
      // Fallback
      console.warn('unknown job member role', proto)
      return 'Champion'
  }
}

function mapJobMemberTypeProtoToCollaboratorType(
  proto: JobProto.MemberType,
): CollaboratorType {
  switch (proto) {
    case JobProto.MemberType.CLIENT:
      return 'Client'
    case JobProto.MemberType.PROVIDER:
      return 'Pro'
    default:
      // Fallback
      console.warn('unknown job member type', proto)
      return 'Client'
  }
}

export function mapJobProtoStatusToStatus(status: StatusProto): JobStatus {
  switch (status) {
    case StatusProto.STATUS_UNSPECIFIED:
      return 'Unknown'
    case StatusProto.UNDER_CONSTRUCTION:
      return 'UnderConstruction'
    case StatusProto.UNCLAIMED:
      return 'Unclaimed'
    case StatusProto.CLAIMED:
      return 'Claimed'
    case StatusProto.CLOSED:
      return 'Closed'
    case StatusProto.IDLE:
      return 'Idle'
    case StatusProto.ON_HOLD:
      return 'OnHold'
    case StatusProto.INFO_GATHERING:
      return 'InfoGathering'
    default:
      console.warn('unknown job status', status)
      return 'Unknown'
  }
}

export function mapJobProtoToTitle(proto: JobProto) {
  return (
    proto.getDescription()?.getValue() ||
    proto.getName()?.getValue() ||
    proto.getLegend()?.getName() ||
    ''
  )
}

export function mapJobProtoToJobType(
  primeUser: PrimeUserFn,
  proto?: JobProto,
): JobTypeProps {
  const typeProto = proto?.getType()

  switch (typeProto?.getTypeCase()) {
    case JobProto.JobType.TypeCase.BOOKKEEPING: {
      return {
        type: 'Bookkeeping',
      }
    }
    case JobProto.JobType.TypeCase.CONSULTATION: {
      const consultationProto = proto?.getConsultation()

      return {
        type: 'Consultation',
        consultation: consultationProto
          ? mapConsultationProtoToConsultation(consultationProto, primeUser)
          : null,
        diyJobId: typeProto.getConsultation()?.getDiyJobId()?.getValue(),
      }
    }
    case JobProto.JobType.TypeCase.TAX: {
      const taxProto = typeProto.getTax()!
      const consultationProto = proto?.getConsultation()

      return {
        type: 'Tax',
        consultation: consultationProto
          ? mapConsultationProtoToConsultation(consultationProto, primeUser)
          : null,
        prepJobId: taxProto.getJobLinks()?.getPrepJobLink()?.getId(),
        reviewJobId: taxProto.getJobLinks()?.getPrepJobLink()?.getId(),
        isExtensionOnlyJob: taxProto.getIsExtensionOnlyJob(),
      }
    }
    case JobProto.JobType.TypeCase.MISC:
    case JobProto.JobType.TypeCase.UNSPECIFIED:
    case JobProto.JobType.TypeCase.TYPE_NOT_SET:
      return {
        type: 'Misc',
      }
    default:
      console.warn('unknown job type', typeProto)
      return {
        type: 'Misc',
      }
  }
}

export function mapJobScopeItemsProtoToJobScopeItems(
  proto: JobProto.JobScopeItem,
): JobScopeItem {
  return {
    id: proto.getId(),
    name: proto.getName(),
    quantity: proto.getQuantity(),
    bundleId: proto.getBundleId()?.getValue() ?? null,
  }
}

export function mapLegendToProps(proto: JobProto.Legend): JobLegend {
  const ratingConfig = proto.getRatingConfig()
  const txfConfig = proto.getTxfConfig()

  return {
    id: Id.assert(proto.getId()),
    version: proto.getVersion(),
    name: proto.getName(),
    serviceType: match(proto.getServiceType(), {
      [JobProto.Legend.LegendServiceType.TAX]: 'Tax',
      [JobProto.Legend.LegendServiceType.BOOKKEEPING]: 'Bookkeeping',
      [JobProto.Legend.LegendServiceType.CONSULTATION]: 'Consultation',
      [JobProto.Legend.LegendServiceType.MISC]: 'Misc',

      // Defaults
      [JobProto.Legend.LegendServiceType.LEGEND_SERVICE_TYPE_UNSPECIFIED]:
        'Misc',
    }),
    isBillingEnabled: !proto.getSkipBilling(),
    isQuoteEnabled: !proto.getSkipQuote(),
    isRequestedProviderSelectionAllowed: proto.getAllowProSelection(),
    ratingConfig: ratingConfig
      ? mapRatingConfigProtoToProps(ratingConfig)
      : JobLegendRatingConfig.default,
    txfConfig: txfConfig
      ? mapTxfConfigProtoToProps(txfConfig)
      : JobLegendTxfConfig.default,
  }
}

function mapTxfConfigProtoToProps(
  proto: JobProto.Legend.TxfConfig,
): JobLegendTxfConfig {
  return {
    showQuestionnaire: proto.getShowQuestionnaire(),
  }
}

function mapRatingConfigProtoToProps(
  proto: JobProto.Legend.RatingConfig,
): JobLegendRatingConfig {
  return {
    starRatingDisabled: proto.getStarRatingDisabled(),
    platformRatingEnabled: proto.getPlatformRatingEnabled(),
    proRatingEnabled: proto.getProRatingEnabled(),
  }
}
